import { createRouter, createWebHistory } from 'vue-router'
import { useRoleGuard } from '@/composables/useRoleGuard'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public / Guest
    {
      path: '/',
      name: 'landing',
      component: () => import('@/pages/public/HomePage.vue'),
    },
    // TODO: re-enable coach feature, see PROJECT_STATUS.md (2026-07-14 meeting notes —
    // "Remove the coach pages for now since it will be implemented in the future").
    // {
    //   path: '/coaches',
    //   name: 'coach-directory',
    //   component: () => import('@/pages/public/CoachDirectoryPage.vue'),
    // },
    // TODO: re-enable tournaments feature, see PROJECT_STATUS.md — "Remove match scoring,
    // coaches, and tournaments for now." Pages left intact under src/pages/public/,
    // src/pages/tournaments/, and src/pages/admin/.
    // {
    //   path: '/tournaments',
    //   name: 'tournament-listing',
    //   component: () => import('@/pages/public/TournamentListingPage.vue'),
    // },
    // {
    //   path: '/tournaments/:id',
    //   name: 'tournament-details',
    //   component: () => import('@/pages/public/TournamentDetailsPage.vue'),
    // },
    // {
    //   path: '/tournaments/:id/bracket',
    //   name: 'tournament-live-bracket',
    //   component: () => import('@/pages/tournaments/LiveBracketPage.vue'),
    // },
    // {
    //   path: '/tournaments/:id/register',
    //   name: 'tournament-registration-form',
    //   component: () => import('@/pages/tournaments/TournamentRegistrationFormPage.vue'),
    // },
    // The Contact page was removed 2026-09-18 — Messenger is the channel. Kept
    // as a redirect so an old bookmark or search result doesn't land on a 404.
    {
      path: '/contact',
      name: 'contact',
      redirect: () => {
        window.location.replace('https://m.me/homecourtiloilo')
        return '/'
      },
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: () => import('@/pages/public/PrivacyPolicyPage.vue'),
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/pages/public/TermsOfServicePage.vue'),
    },
    { path: '/login', name: 'login', component: () => import('@/pages/public/LoginPage.vue') },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/pages/public/RegisterPage.vue'),
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/pages/public/ForgotPasswordPage.vue'),
    },
    {
      path: '/booking',
      name: 'booking',
      component: () => import('@/pages/public/BookingPage.vue'),
    },
    // Where a booking goes the instant it's created: it exists and holds the
    // slot, but it's `pending` until staff verify payment. Public and
    // unguarded — a guest books without an account, so nothing here can
    // require one. Everything it renders comes from what createBooking
    // returned (kept in sessionStorage), not from the route: the bookingId in
    // the path only says *which* stored record to show, and the page renders
    // nothing at all if there isn't one.
    {
      path: '/booking/pay/:bookingId',
      name: 'booking-payment',
      component: () => import('@/pages/public/BookingPaymentPage.vue'),
    },
    // Read-only "where's my booking?" lookup. Public and unguarded for the
    // same reason the payment page is: the customers who most need it are
    // guests with no account, who cannot read their own booking doc. It
    // exposes no mutation — see lookupBookingStatus.
    {
      path: '/booking/status',
      name: 'booking-status',
      component: () => import('@/pages/public/BookingStatusPage.vue'),
    },
    {
      path: '/court-status',
      name: 'court-status',
      component: () => import('@/pages/public/CourtStatusPage.vue'),
    },
    // Lobby-TV display — chrome-free, fills the whole screen, no scroll.
    // Not linked from anywhere in the UI; meant to be opened directly by
    // whoever sets up the TV (see CourtStatusTvPage.vue's own comment).
    {
      path: '/court-status/tv',
      name: 'court-status-tv',
      component: () => import('@/pages/public/CourtStatusTvPage.vue'),
    },
    // Per-court stacking display — chrome-free, one court, meant for a TV
    // dedicated to that court, or opened from Court Assignment's own
    // "Display" link (see CourtAssignmentPage.vue's openCourtDisplay).
    {
      path: '/courts/:courtId/display',
      name: 'court-display-tv',
      component: () => import('@/pages/public/CourtDisplayTvPage.vue'),
    },

    // Player Panel
    {
      path: '/player/dashboard',
      name: 'player-dashboard',
      // requiresRole is deliberately absent on THIS route only.
      //
      // The original reason — "no Cloud Function sets the role claim yet" —
      // is obsolete: setUserRole has shipped and has live callers in
      // UserManagementPage.vue. The reason it stays off is the backlog it
      // left behind. The claim is only set when an admin approves someone, so
      // every account from before that existed still has none. Measured in
      // production 2026-09-18: 1,789 of 2,161 Auth accounts carry a role
      // claim, 372 do not. Adding the guard today logs ~17% of real customers
      // out of their own dashboard.
      //
      // So: backfill the claim first, then add the guard. Anything sooner is
      // a lockout, not a fix.
      //
      // coach/staff/admin dashboards keep their guard regardless: those
      // panels' sidebars expose real management tools (Courts, Users, etc.)
      // that a 'guest' has no business seeing, let alone reaching — ungating
      // those let any signed-in guest browse the admin/staff panel shell and
      // click into admin-only pages (dead-ending at /not-found, but visible).
      // See PROJECT_STATUS.md §6 P0.
      component: () => import('@/pages/player/DashboardPage.vue'),
    },
    {
      path: '/player/book',
      name: 'player-book-court',
      meta: { requiresRole: 'player' },
      component: () => import('@/pages/player/BookCourtPage.vue'),
    },
    {
      path: '/player/bookings',
      name: 'player-my-bookings',
      meta: { requiresRole: 'player' },
      component: () => import('@/pages/player/MyBookingsPage.vue'),
    },
    // TODO: re-enable coach feature, see PROJECT_STATUS.md (2026-07-14 meeting notes).
    // {
    //   path: '/player/coaches',
    //   name: 'player-book-coach',
    //   meta: { requiresRole: 'player' },
    //   component: () => import('@/pages/player/BookCoachPage.vue'),
    // },
    // TODO: re-enable match scoring/tournaments feature, see PROJECT_STATUS.md — "Remove
    // match scoring, coaches, and tournaments for now." Pages left intact under src/pages/player/.
    // {
    //   path: '/player/matches',
    //   name: 'player-match-history',
    //   meta: { requiresRole: 'player' },
    //   component: () => import('@/pages/player/MatchHistoryPage.vue'),
    // },
    // {
    //   path: '/player/matches/:id/score',
    //   name: 'player-live-match-scoring',
    //   meta: { requiresRole: 'player' },
    //   component: () => import('@/pages/player/LiveMatchScoringPage.vue'),
    // },
    // {
    //   path: '/player/tournaments',
    //   name: 'player-tournament-registration',
    //   meta: { requiresRole: 'player' },
    //   component: () => import('@/pages/player/TournamentRegistrationPage.vue'),
    // },
    {
      path: '/player/profile',
      name: 'player-profile',
      meta: { requiresRole: 'player' },
      component: () => import('@/pages/player/ProfileSettingsPage.vue'),
    },

    // Coach Panel — TODO: re-enable coach feature, see PROJECT_STATUS.md
    // (2026-07-14 meeting notes). Pages left intact under src/pages/coach/.
    // {
    //   path: '/coach/dashboard',
    //   name: 'coach-dashboard',
    //   meta: { requiresRole: 'coach' },
    //   component: () => import('@/pages/coach/DashboardPage.vue'),
    // },
    // {
    //   path: '/coach/availability',
    //   name: 'coach-availability',
    //   meta: { requiresRole: 'coach' },
    //   component: () => import('@/pages/coach/AvailabilityManagementPage.vue'),
    // },
    // {
    //   path: '/coach/sessions',
    //   name: 'coach-sessions',
    //   meta: { requiresRole: 'coach' },
    //   component: () => import('@/pages/coach/SessionsPage.vue'),
    // },
    // {
    //   path: '/coach/players',
    //   name: 'coach-player-roster',
    //   meta: { requiresRole: 'coach' },
    //   component: () => import('@/pages/coach/PlayerRosterPage.vue'),
    // },
    // {
    //   path: '/coach/profile',
    //   name: 'coach-profile',
    //   meta: { requiresRole: 'coach' },
    //   component: () => import('@/pages/coach/ProfileSettingsPage.vue'),
    // },

    // Staff Panel
    {
      path: '/staff/courts',
      name: 'staff-court-assignment',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/CourtAssignmentPage.vue'),
    },
    {
      path: '/staff/bookings',
      name: 'staff-booking-management',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/BookingManagementPage.vue'),
    },
    {
      // This is an internal item-tracking tool for staff, not a formal
      // checkout system — the URL and page copy are both deliberately
      // neutral about that.
      path: '/staff/item-sales',
      name: 'staff-item-sales',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/ItemSalesPage.vue'),
    },
    {
      path: '/staff/inventory',
      name: 'staff-inventory',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/InventoryManagementPage.vue'),
    },
    {
      path: '/staff/sales',
      name: 'staff-daily-sales-summary',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/DailySalesSummaryPage.vue'),
    },
    {
      path: '/staff/players',
      name: 'staff-players',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/PlayersPage.vue'),
    },
    {
      path: '/staff/offline',
      name: 'staff-offline-mode',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/OfflineModePage.vue'),
    },
    {
      path: '/staff/courts-management',
      name: 'staff-court-management',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/CourtManagementPage.vue'),
    },
    {
      path: '/staff/matches',
      name: 'staff-matches',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/MatchesPage.vue'),
    },
    {
      // One route for scoring and results, not two — MatchScoringPage.vue
      // branches on the match's own status (in-progress renders the live
      // tap UI, completed renders a read-only results layout), same
      // status-driven-branching convention BookingReservationCard.vue uses.
      path: '/staff/matches/:id/score',
      name: 'staff-match-scoring',
      meta: { requiresRole: ['staff', 'admin'] },
      component: () => import('@/pages/staff/MatchScoringPage.vue'),
    },

    // Admin Panel
    {
      path: '/admin/dashboard',
      name: 'admin-overview',
      meta: { requiresRole: 'admin' },
      component: () => import('@/pages/admin/OverviewPage.vue'),
    },
    {
      path: '/admin/reports',
      name: 'admin-sales-reporting',
      meta: { requiresRole: 'admin' },
      component: () => import('@/pages/admin/SalesReportingPage.vue'),
    },
    {
      path: '/admin/users',
      name: 'admin-user-management',
      meta: { requiresRole: 'admin' },
      component: () => import('@/pages/admin/UserManagementPage.vue'),
    },
    {
      path: '/admin/settings',
      name: 'admin-system-settings',
      meta: { requiresRole: 'admin' },
      component: () => import('@/pages/admin/SystemSettingsPage.vue'),
    },

    // Fallback
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/public/NotFoundPage.vue'),
    },
  ],
})

router.beforeEach(useRoleGuard())

export default router
